/* eslint-disable max-lines */
import {
  boolean,
  integer,
  foreignKey,
  index,
  pgEnum,
  pgTable,
  text,
  timestamp,
  varchar,
  doublePrecision,
  primaryKey,
  jsonb,
  check,
  type PgColumn,
  pgView,
} from 'drizzle-orm/pg-core';
import {createInsertSchema, createUpdateSchema} from 'drizzle-zod';
import {generateId} from './generateId';
import z from 'zod';
import {mapObjectValues} from '@features/utils/object';
import {sql} from 'drizzle-orm';
import {pgMessageAttachmentColumn} from './pgMessageAttachmentColumn';
import {pgRewardSubmissionColumn} from './pgRewardSubmissionColumn';
import {pgScreenPositionsColumn} from './pgScreenPositionsColumn';
import {users} from './auth-schema';

export const usersTable = users;

export type PendingStyleUpdates = Record<string, string>;

// Legacy html/css theme font set. This is the db-valid subset — Taste-only
// fonts (added later for the components genType) are intentionally excluded so
// they can't be written to legacy theme rows. See FontKey in api/chat/schema.
export const fontFamilyValues = [
  // Curated Dinamo set used by the taste-engine POC
  'abcDiatype',
  'abcDiatypeRounded',
  'abcFavorit',
  'abcFavoritMono',
  'abcGramercy',
  'abcGravity',
  'abcMarfaMono',
  'abcMarist',
  'abcOtto',
  'abcSocial',
  'abcWhyte',
  // Legacy Google fonts kept so existing rows still validate
  'inter',
  'dmSans',
  'nunito',
  'ptSerif',
  'spaceGrotesk',
  'ibmPlexMono',
  'ibmPlexSans',
  'robotoSlab',
  'geist',
  'shantellSans',
] as const;
export const fontFamilyEnum = pgEnum('font_family', fontFamilyValues);

const id = (name?: string) =>
  name !== undefined ? varchar(name, {length: 255}) : varchar({length: 255});

export const subscriptionBillingInterval = pgEnum('billing_interval', [
  'year',
  'month',
  'week',
  'day',
  'not-recurring',
]);

export const subscriptionTier = pgEnum('subscription_tier', [
  'plus',
  'pro',
  'free',
]);

export const subscriptionsTable = pgTable(
  'subscriptions',
  {
    userId: varchar({length: 255})
      .primaryKey()
      .references(() => usersTable.id, {
        onDelete: 'cascade',
      }),
    isActive: boolean().notNull(),
    // Stripe status is `past_due`: still counts as active (grace period) but all
    // generation/export is locked in the UI until payment succeeds.
    pastDue: boolean().notNull().default(false),
    creditsUsed: doublePrecision('screen_count').notNull(),
    creditsUseTimestamp: timestamp('last_screen_record_time'),
    figmaExportCount: integer().notNull().default(0),
    lastFigmaExportTime: timestamp(),
    sessionWindowStart: timestamp(),
    sessionRequestCount: integer().notNull().default(0),
    dayWindowStart: timestamp(),
    dayRequestCount: integer().notNull().default(0),
    createdAt: timestamp().notNull().defaultNow(),
    emailPayment: text(),
    stripeSubscriptionId: varchar({length: 255}).unique(),
    billingInterval: subscriptionBillingInterval(),
    billingCycleStart: timestamp(),
    tier: subscriptionTier(),
    updatedAt: timestamp()
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
  },
  (st) => [
    index('indiv_subscription_by_stripe_id').on(st.stripeSubscriptionId),
    index().on(st.userId),
  ],
);

export const teamSubscriptionsTable = pgTable(
  'teamSubscriptions',
  {
    teamId: id()
      .primaryKey()
      .references(() => teamsTable.id, {onDelete: 'cascade'}),
    isActive: boolean().notNull(),
    pastDue: boolean().notNull().default(false),
    stripeSubscriptionId: varchar({length: 255}).unique().notNull(),
    billingInterval: subscriptionBillingInterval(),
    billingCycleStart: timestamp(),
    tier: subscriptionTier(),
    emailPayment: text().notNull(),
    createdAt: timestamp().notNull().defaultNow(),
    updatedAt: timestamp()
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
  },
  (st) => [index('team_subscription_by_stripe_id').on(st.stripeSubscriptionId)],
);

/**
 * A bonus credit can both apply to a user, and to a team.
 * The bonusCreditsView resolves this automatically, so that you can just query that one to see which credits you have.
 */
export const bonusCreditsTable = pgTable(
  'bonus_credits',
  {
    id: id().primaryKey().$defaultFn(generateId),
    displayName: text().notNull(),
    userId: id().references(() => usersTable.id, {onDelete: 'set null'}),
    teamId: id().references(() => teamsTable.id, {onDelete: 'set null'}),
    numCredits: doublePrecision().notNull(),
    creditsUsed: doublePrecision().notNull(),
    createdAt: timestamp().notNull().defaultNow(),
    updatedAt: timestamp()
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
    expiresAt: timestamp(),
    // Set for credit packs bought via Stripe. Unique so a paid invoice can only
    // ever grant one bonus row (idempotency for retries / double-delivery).
    stripeInvoiceId: varchar({length: 255}).unique(),
  },
  (tbl) => [index().on(tbl.userId), index().on(tbl.teamId)],
);

export const bonusCreditsView = pgView('user_bonus_credits', {
  forUser: id('for_user').notNull(),
  id: id().notNull(),
  displayName: text('display_name').notNull(),
  userId: id('user_id'),
  teamId: id('team_id'),
  numCredits: integer('num_credits').notNull(),
  creditsUsed: integer('credits_used').notNull(),
  createdAt: timestamp('created_at').notNull(),
  updatedAt: timestamp('updated_at').notNull(),
  expiresAt: timestamp('expires_at'),
}).as(sql`
  SELECT
    user_id as for_user,
    *
  FROM
    bonus_credits
  WHERE
    user_id IS NOT NULL
  UNION (
    SELECT
      tm.user_id as for_user,
      bc.*
    FROM
      team_members tm,
      bonus_credits bc
    WHERE
      bc.team_id = tm.team_id
  )`);

export const creditUsageTable = pgTable(
  'credit_usage',
  {
    user: id().notNull(),
    createdAt: timestamp().notNull(),
    creditSource: text().notNull(),
    data: jsonb(),
  },
  (tbl) => [primaryKey({columns: [tbl.user, tbl.createdAt]})],
);

export const aiUsageStatusEnum = pgEnum('ai_usage_status', [
  'ok',
  'error',
  'aborted',
]);

/**
 * Model price history. Price changes are INSERTs with a new validFrom — never
 * edit rows, so historical ai_usage costs stay interpretable.
 * All token prices are $ per 1M tokens; perImage is $ per generated image.
 */
export const modelPricesTable = pgTable(
  'model_prices',
  {
    id: id().primaryKey().$defaultFn(generateId),
    model: text().notNull(),
    validFrom: timestamp().notNull().defaultNow(),
    inputPerM: doublePrecision().notNull().default(0),
    outputPerM: doublePrecision().notNull().default(0),
    cacheReadPerM: doublePrecision().notNull().default(0),
    cacheWritePerM: doublePrecision().notNull().default(0),
    perImage: doublePrecision().notNull().default(0),
  },
  (t) => [index().on(t.model, t.validFrom.desc())],
);

/**
 * Append-only AI usage ledger: one row per provider call (= per step of a
 * multi-step generation turn; turnId groups a turn). Records ALL calls
 * regardless of tier or outcome — measurement is unconditional, billing is a
 * separate policy on top (credit_usage). inputTokens is the non-cached
 * portion; cost = input×inputPerM + cacheRead×cacheReadPerM +
 * cacheWrite×cacheWritePerM + output×outputPerM (+ imageCount×perImage).
 */
export const aiUsageTable = pgTable(
  'ai_usage',
  {
    id: id().primaryKey().$defaultFn(generateId),
    createdAt: timestamp().notNull().defaultNow(),
    userId: id(),
    tierAtUse: text(),
    teamId: id(),
    flowId: id(),
    turnId: id(),
    functionId: text(),
    provider: text().notNull(),
    // The runtime model id (an inference-profile ARN for Bedrock). For cost
    // analytics ALWAYS group by billingModel — costUsd is priced from it, and
    // model/billingModel intentionally diverge (e.g. gemini-3.5-flash runs
    // billed under the requested gemini name).
    model: text().notNull(),
    billingModel: text().notNull(),
    serviceTier: text(),
    inputTokens: integer().notNull().default(0),
    cacheReadTokens: integer().notNull().default(0),
    cacheWriteTokens: integer().notNull().default(0),
    outputTokens: integer().notNull().default(0),
    reasoningTokens: integer().notNull().default(0),
    imageCount: integer().notNull().default(0),
    costUsd: doublePrecision().notNull().default(0),
    priceVersion: id(),
    status: aiUsageStatusEnum().notNull().default('ok'),
    latencyMs: integer(),
    environment: text().notNull(),
  },
  (t) => [
    index().on(t.userId, t.createdAt.desc()),
    index().on(t.createdAt),
    index().on(t.flowId),
  ],
);

export const aiUsageMonthlyView = pgView('ai_usage_monthly', {
  month: timestamp().notNull(),
  userId: id('user_id'),
  tierAtUse: text('tier_at_use'),
  teamId: id('team_id'),
  costUsd: doublePrecision('cost_usd').notNull(),
  inputTokens: doublePrecision('input_tokens').notNull(),
  cacheReadTokens: doublePrecision('cache_read_tokens').notNull(),
  cacheWriteTokens: doublePrecision('cache_write_tokens').notNull(),
  outputTokens: doublePrecision('output_tokens').notNull(),
  imageCount: doublePrecision('image_count').notNull(),
  calls: doublePrecision().notNull(),
  turns: doublePrecision().notNull(),
}).as(sql`
  SELECT
    date_trunc('month', created_at) AS month,
    user_id,
    tier_at_use,
    team_id,
    sum(cost_usd)::float8 AS cost_usd,
    sum(input_tokens)::float8 AS input_tokens,
    sum(cache_read_tokens)::float8 AS cache_read_tokens,
    sum(cache_write_tokens)::float8 AS cache_write_tokens,
    sum(output_tokens)::float8 AS output_tokens,
    sum(image_count)::float8 AS image_count,
    count(*)::float8 AS calls,
    count(DISTINCT turn_id)::float8 AS turns
  FROM ai_usage
  WHERE environment = 'production'
  GROUP BY 1, 2, 3, 4`);

export const aiUsageDailyByFunctionView = pgView('ai_usage_daily_by_function', {
  day: timestamp().notNull(),
  functionId: text('function_id'),
  billingModel: text('billing_model').notNull(),
  status: text().notNull(),
  costUsd: doublePrecision('cost_usd').notNull(),
  calls: doublePrecision().notNull(),
  turns: doublePrecision().notNull(),
}).as(sql`
  SELECT
    date_trunc('day', created_at) AS day,
    function_id,
    billing_model,
    status,
    sum(cost_usd)::float8 AS cost_usd,
    count(*)::float8 AS calls,
    count(DISTINCT turn_id)::float8 AS turns
  FROM ai_usage
  WHERE environment = 'production'
  GROUP BY 1, 2, 3, 4`);

export const teamsTable = pgTable('teams', {
  id: id().primaryKey().$defaultFn(generateId),
  name: varchar({length: 1024}),
  avatarUrl: text(),
  createdAt: timestamp().notNull().defaultNow(),
  updatedAt: timestamp()
    .notNull()
    .defaultNow()
    .$onUpdate(() => new Date()),
});

export const teamRoleEnum = pgEnum('teamrole', ['owner', 'editor']);
export const teamMembersTable = pgTable(
  'team_members',
  {
    teamId: id()
      .notNull()
      .references(() => teamsTable.id, {onDelete: 'cascade'}),
    userId: id()
      .notNull()
      .references(() => usersTable.id),
    role: teamRoleEnum().notNull(),
    createdAt: timestamp().notNull().defaultNow(),
    updatedAt: timestamp()
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
  },
  (table) => [
    primaryKey({columns: [table.teamId, table.userId]}),
    index().on(table.teamId),
    index().on(table.userId),
  ],
);

export const teamInviteStatusEnum = pgEnum('team_invite_status', [
  'pending',
  'accepted',
  'declined',
]);
export const teamInvitesTable = pgTable(
  'team_invites',
  {
    id: id().primaryKey().$defaultFn(generateId),
    teamId: id()
      .notNull()
      .references(() => teamsTable.id, {onDelete: 'cascade'}),
    email: text().notNull(),
    role: teamRoleEnum().notNull(),
    status: teamInviteStatusEnum().notNull(),
    createdBy: id()
      .notNull()
      .references(() => usersTable.id, {onDelete: 'cascade'}),

    createdAt: timestamp().notNull().defaultNow(),
    updatedAt: timestamp()
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
    finalizedAt: timestamp(),
    finalizedBy: id().references(() => usersTable.id, {
      onDelete: 'set null',
    }),
  },
  (tbl) => [
    index().on(tbl.email),
    index().on(tbl.teamId),
    index().on(tbl.finalizedBy),
    index().on(tbl.createdBy),
  ],
);

export const genTypeEnum = pgEnum('gentype', [
  'web',
  'web-html-css',
  'mobile',
  'mobile-html-css',
  'components',
]);
export const modelEnum = pgEnum('model', [
  'gpt-5',
  'auto',
  'gemini-3.1-pro',
  'gemini-3-flash',
  'gpt-5.4-mini',
]);

export const flowsTable = pgTable(
  'flows',
  {
    id: id().primaryKey().$defaultFn(generateId),
    userId: id()
      .notNull()
      .references(() => usersTable.id, {onDelete: 'cascade'})
      .$defaultFn(generateId),
    teamId: id(), // TODO: Foreign Key
    createdAt: timestamp().notNull().defaultNow(),
    projectName: varchar({length: 255}),
    cover: text(),
    genType: genTypeEnum().notNull(),
    model: modelEnum(),
    themeId: id(), // TODO: Foreign Key
    referenceStructure: text(),
    originalFlowId: id(),
    referenceImageUrl: text(),
    pendingReferenceImage: text(),
    pendingFigmaScreenshot: text(),
    pendingFigmaHtml: text(),
    pendingFigmaUrl: text(),
    pendingFigmaLastModified: text(),
    summary: text(),
    isThemeLocked: boolean().default(false),
    updatedAt: timestamp()
      .$defaultFn(() => new Date())
      .$onUpdate(() => new Date()),

    // can't be made a primary key, sad.
    activeVersion: id(),
  },
  (table) => [
    // Can't be added using .references( syntax, circular dep breaks type inference.
    foreignKey({
      columns: [table.originalFlowId],
      foreignColumns: [table.id],
    }).onDelete('set null'),
    index('flows_for_user').on(
      table.userId.asc(),
      table.originalFlowId,
      table.createdAt.desc(),
    ),
    index('flows_for_team').on(table.teamId),
    index().on(table.originalFlowId),
    index().on(table.themeId),
    // Indexes for sorting by COALESCE(updatedAt, createdAt). Migrations are handled separately.
    index('flows_user_updated').on(table.userId, table.updatedAt.desc()),
    index('flows_team_updated').on(table.teamId, table.updatedAt.desc()),
  ],
);

export const flowVersionsTable = pgTable(
  'flow_versions',
  {
    id: id().primaryKey().$defaultFn(generateId),
    flow: id()
      .references(() => flowsTable.id, {onDelete: 'cascade'})
      .notNull(),
    createdAt: timestamp().notNull().defaultNow(),
    message: id().references(() => messagesTable.id, {onDelete: 'cascade'}),
    predecessor: id(),
    fsSnapshot: id()
      .references(() => fsSnapshot.id)
      .notNull(),
    output: id()
      .references(() => fsSnapshot.id)
      .notNull(),
    pendingStyleUpdates: jsonb().$type<PendingStyleUpdates>(),
    screenPositions: pgScreenPositionsColumn().notNull().default({}),
  },
  (table) => [index().on(table.flow, table.predecessor)],
);

export const flowConversationsTable = pgTable(
  'flow_conversations',
  {
    flow: id()
      .references(() => flowsTable.id, {onDelete: 'cascade'})
      .notNull(),
    byUser: id()
      .notNull()
      .references(() => usersTable.id, {onDelete: 'cascade'}),
    state: jsonb().notNull(),
    seenFiles: text().array().array(),
    createdAt: timestamp().notNull().defaultNow(),
    updatedAt: timestamp().notNull().defaultNow(),
  },
  (tbl) => [
    primaryKey({columns: [tbl.flow, tbl.byUser]}),
    index().on(tbl.byUser),
  ],
);

export const screensTable = pgTable(
  'screens',
  {
    id: id().primaryKey().$defaultFn(generateId),
    flowId: id()
      .notNull()
      .references(() => flowsTable.id, {onDelete: 'cascade'}),

    screenName: varchar({length: 128}),
    activeStructureId: id(),
    themeId: id().references(() => themesTable.id),

    /** This is the timestamp used for ordering, so we save it as number. */
    index: doublePrecision().notNull(),

    x: doublePrecision(),
    y: doublePrecision(),

    /** Information about the creation date of the screen */
    createdAt: timestamp().notNull().defaultNow(),
  },
  (table) => [index().on(table.flowId), index().on(table.themeId)],
);

// @deprecated in favor of flowVersions.screenPositions
export const componentScreenPositionsTable = pgTable(
  'component_screen_positions',
  {
    flowId: id()
      .notNull()
      .references(() => flowsTable.id, {onDelete: 'cascade'}),
    screenId: varchar({length: 1024}).notNull(),
    x: doublePrecision().notNull(),
    y: doublePrecision().notNull(),
    createdAt: timestamp().notNull().defaultNow(),
  },
  (table) => [
    primaryKey({columns: [table.flowId, table.screenId]}),
    index().on(table.flowId),
  ],
);

export const senderEnum = pgEnum('sender', ['assistant', 'user']);
export const messagesTable = pgTable(
  'messages',
  {
    id: id().primaryKey().$defaultFn(generateId),
    screenId: id().notNull(),
    text: text().notNull(),
    sender: senderEnum().notNull(),
    userId: id().references(() => usersTable.id, {onDelete: 'set null'}),
    screenName: varchar({length: 128}),
    structureId: id().references(() => structuresTable.id satisfies PgColumn, {
      onDelete: 'set null',
    }),

    /** @deprecated switch to message attachments */
    referenceImageUrl: text(),

    /** @deprecated switch to message attachments */
    figmaHtml: text(),
    /** @deprecated switch to message attachments */
    figmaScreenshotUrl: text(),

    /** @deprecated switch to message attachments */
    selectedHtml: text(),

    createdAt: timestamp().notNull().defaultNow(),
    updatedAt: timestamp()
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
  },
  (tbl) => [
    index().on(tbl.screenId),
    index().on(tbl.structureId),
    index().on(tbl.userId),
    index().on(tbl.createdAt),
  ],
);

export const messageAttachmentsTable = pgTable(
  'message_attachments',
  {
    messageId: id()
      .references(() => messagesTable.id, {
        onDelete: 'cascade',
      })
      .notNull(),
    order: integer().notNull(),
    data: pgMessageAttachmentColumn().notNull(),
  },
  (tbl) => [
    primaryKey({
      columns: [tbl.messageId, tbl.order],
    }),
  ],
);

export const clarifyingQuestionsTable = pgTable(
  'clarifying_questions',
  {
    screenId: id().primaryKey(),
    userId: id()
      .notNull()
      .references(() => usersTable.id, {onDelete: 'cascade'}),
    questions: jsonb().notNull(),
    answers: jsonb(),
    createdAt: timestamp().notNull().defaultNow(),
  },
  (tbl) => [index().on(tbl.userId)],
);

export const pendingMessageStateEnum = pgEnum('pending_message_state', [
  'waiting',
  'processing',
]);
export const pendingMessagesTable = pgTable(
  'pending_messages',
  {
    messageId: id()
      .primaryKey()
      .references(() => messagesTable.id, {
        onDelete: 'cascade',
      }),
    // Technically, this is redunant (we have the messageId)
    // But I don't wanna `join` in an update query.
    screenId: id().notNull(),
    state: pendingMessageStateEnum().notNull().default('waiting'),
    attempts: integer().notNull().default(0),

    // Detected genType for this generation (from LLM detection)
    // Used by postprocess to set structure.genType
    genType: genTypeEnum(),

    createdAt: timestamp().notNull().defaultNow(),
    updatedAt: timestamp()
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
  },
  (tbl) => [index().on(tbl.screenId, tbl.state)],
);

export const structuresTable = pgTable(
  'structures',
  {
    id: id().primaryKey().$defaultFn(generateId),
    screenId: id()
      .notNull()
      .references(() => screensTable.id, {onDelete: 'cascade'}),

    // We should probably not add too many different kinds of states.
    htmlCss: text(),
    legacyComponents: jsonb(),
    themeId: id().references(() => themesTable.id, {
      onDelete: 'set null',
    }),

    // Per-structure genType for mixed-platform projects
    // Falls back to flow.genType for backwards compatibility
    genType: genTypeEnum(),

    // Screenshot of this specific structure version
    screenshotUrl: text(),

    createdAt: timestamp().notNull().defaultNow(),
    updatedAt: timestamp()
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
  },
  (tbl) => [
    index().on(tbl.screenId),
    index().on(tbl.themeId), // speed up foreign key delete
    check(
      'must_have_data',
      // One of them must be null, the other one can't be null.
      // (if htmlCssState != null, then compontentState must be == NULL)
      // in theory, we could look at gentype of the flow, but that seems too much
      sql`(${tbl.htmlCss} IS NULL) != (${tbl.legacyComponents} IS NULL)`,
    ),
  ],
);

export const imageQuerySource = pgEnum('image_query_source', [
  'unsplash',
  'gemini-2.5-image',
  'gpt-image-2',
  // Similar image above the paid threshold.
  'reused',
  // Closest match for a free or out-of-budget user; a paying user upgrades it.
  'reused-weak',
]);

export const imageQueriesTable = pgTable(
  'imageQueries',
  {
    query: text().primaryKey(),
    createdAt: timestamp().notNull().defaultNow(),
    source: imageQuerySource().notNull().default('unsplash'),
    url: text().notNull(),
    generatingUser: id().references(() => usersTable.id, {
      onDelete: 'set null',
    }),
  },
  (iq) => [index('createdAt').on(iq.createdAt), index().on(iq.generatingUser)],
);

export const pendingImagesTable = pgTable('pending_images', {
  query: text().primaryKey(),
  createdAt: timestamp().notNull().defaultNow(),
});

export const llmImageUrlCacheTable = pgTable(
  'llm_image_url_cache',
  {
    sourceUrl: text().primaryKey(),
    resolvedUrl: text().notNull(),
    expiresAt: timestamp()
      .notNull()
      .default(sql`now() + interval '30 days'`),
    createdAt: timestamp().notNull().defaultNow(),
    updatedAt: timestamp()
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
  },
  (tbl) => [index('llm_image_url_cache_expires_at_idx').on(tbl.expiresAt)],
);

export const likesTable = pgTable('likes', {
  structureId: id().primaryKey(),
  like: boolean().notNull(),
  createdAt: timestamp().notNull().defaultNow(),
  updatedAt: timestamp()
    .notNull()
    .defaultNow()
    .$onUpdate(() => new Date()),
});

export const figmaTokensTable = pgTable(
  'figma_tokens',
  {
    userId: id()
      .primaryKey()
      .references(() => usersTable.id, {onDelete: 'cascade'}),
    token: text().notNull(),
    createdAt: timestamp().notNull().defaultNow(),
    updatedAt: timestamp()
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
  },
  (tbl) => [index().on(tbl.userId)],
);

export const figmaReferencesTable = pgTable(
  'figma_references',
  {
    id: id().primaryKey().$defaultFn(generateId),
    flowId: id()
      .notNull()
      .references(() => flowsTable.id, {onDelete: 'cascade'}),
    figmaUrl: text().notNull(),
    html: text().notNull(),
    screenshotUrl: text().notNull(),
    lastModified: text(),
    createdAt: timestamp().notNull().defaultNow(),
    updatedAt: timestamp()
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
  },
  (tbl) => [index().on(tbl.flowId)],
);

export const workspaceStateTable = pgTable(
  'workspace_state',
  {
    userId: id()
      .primaryKey()
      .references(() => usersTable.id, {onDelete: 'cascade'}),
    lastOpenFlowId: id().references(() => flowsTable.id, {
      onDelete: 'set null',
    }),
    selectedScreenIds: jsonb().$type<string[]>().notNull().default([]),
    updatedAt: timestamp()
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
  },
  (tbl) => [index().on(tbl.userId)],
);

export const apiTokensTable = pgTable(
  'api_tokens',
  {
    id: id().primaryKey().$defaultFn(generateId),
    userId: id()
      .notNull()
      .references(() => usersTable.id, {onDelete: 'cascade'}),
    tokenHash: text().notNull(),
    tokenPrefix: varchar({length: 12}).notNull(),
    name: varchar({length: 255}).default('Claude Code MCP'),
    lastUsedAt: timestamp(),
    createdAt: timestamp().notNull().defaultNow(),
    revokedAt: timestamp(),
  },
  (tbl) => [index().on(tbl.userId), index().on(tbl.tokenHash)],
);

export const generationFailuresTable = pgTable('generation_failures', {
  createdAt: timestamp().notNull().defaultNow(),
  model: id(),
  flow: text(),
  structure: id(),
  user: id(),
  error: text(),
  timeout: boolean(),
});

export const htmlFlowToComponentsErrors = pgTable(
  'html_flow_conversion_errors',
  {
    createdAt: timestamp().notNull().defaultNow(),
    flow: id().notNull(),
    error: text(),
    sentryId: text(),
  },
);

export const radiusThemeProperties = {
  '--radius-sm': {size: 'Small', example: 4},
  '--radius-md': {size: 'Medium', example: 6},
  '--radius-lg': {size: 'Large', example: 8},
  '--radius-xl': {size: 'Extra Large (e.g. for pills)', example: 12},
} as const;

export const colorThemeProperties = {
  '--background': 'Main app background color',
  '--foreground': 'Default text color for content and UI',
  '--border': 'Borders for inputs, cards, and containers',
  '--input': 'Background for input fields and text areas',
  '--primary': 'Main interactive elements (buttons, links)',
  '--primary-foreground': 'Text color inside primary buttons',
  '--secondary': 'Secondary sections background (panels, badges)',
  '--secondary-foreground': 'Text or icons on secondary backgrounds',

  '--muted': 'Background for disabled or low-priority items',
  '--muted-foreground': 'Text for disabled or secondary actions',
  '--success': 'Background for success messages and badges',
  '--success-foreground': 'Text on success backgrounds',
  '--accent': 'Special highlights or attention elements',
  '--accent-foreground': 'Text on accent highlights',
  '--destructive': 'Background for critical errors or delete actions',

  '--destructive-foreground': 'Text on destructive backgrounds',
  '--warning': 'Background for warnings and alerts',
  '--warning-foreground': 'Text on warning backgrounds',
  '--card': 'Background for card containers',
  '--card-foreground': 'Main text inside cards',
  '--sidebar': 'Background for side navigation areas',
  '--sidebar-foreground': 'Text and icons inside sidebar',
  '--sidebar-primary': 'Highlighted/active sidebar item background',

  '--sidebar-primary-foreground': 'Text for active sidebar items',
} as const;

const themeProperty = () => varchar({length: 255}).notNull();

export const themesTable = pgTable(
  'themes',
  {
    id: id().primaryKey().$defaultFn(generateId),
    name: varchar({length: 255}).notNull(),
    createdAt: timestamp().notNull().defaultNow(),
    userId: id().references(() => usersTable.id, {
      onDelete: 'cascade',
    }),

    ...mapObjectValues(radiusThemeProperties, themeProperty),
    ...mapObjectValues(colorThemeProperties, themeProperty),

    fontFamilyBody: fontFamilyEnum().notNull(),
  },
  (tbl) => [index().on(tbl.userId, tbl.createdAt)],
);

export const flagsTable = pgTable('flags', {
  name: varchar({length: 64}).primaryKey(),
  value: jsonb().notNull(),
});

export const closedAlphaUsersTable = pgTable('closed_alpha_users', {
  email: varchar({length: 255}).primaryKey(),
});

export const fsSnapshot = pgTable(
  'snapshots',
  {
    id: id().primaryKey().$defaultFn(generateId),
    predecessor: id(),
    createdAt: timestamp().notNull().defaultNow(),
  },
  (t) => [
    foreignKey({columns: [t.predecessor], foreignColumns: [t.id]})
      .onUpdate('set null')
      .onDelete('set null'),
  ],
);

export const fsFile = pgTable('files', {
  hash: id().primaryKey(),
  contents: text().notNull(),
});

export const fsSnapshotFiles = pgTable(
  'snapshot_files',
  {
    snapshot: id()
      .references(() => fsSnapshot.id, {
        onDelete: 'cascade',
        onUpdate: 'cascade',
      })
      .notNull(),
    path: text().notNull(),
    file: id()
      .references(() => fsFile.hash, {
        onDelete: 'restrict',
        onUpdate: 'restrict',
      })
      .notNull(),
  },
  (t) => [primaryKey({columns: [t.snapshot, t.path]})],
);

export const vercelSandboxSnapshots = pgTable('vercel_sandbox_snapshots', {
  snapshotId: id().primaryKey(),
  /** What this sandbox is about + version */
  version: id().notNull(),
  createdAt: timestamp().notNull(),
  expiresAt: timestamp(),
});

export const vercelSandboxActive = pgTable('vercel_active_sandboxes', {
  sandboxId: id().primaryKey(),
  flow: id().notNull(),
  snapshotVersion: id().notNull(),
  createdAt: timestamp().notNull(),
  expiresAt: timestamp().notNull(),
});

export const rewardSubmissionStatus = pgEnum('reward_submission_status', [
  'submitted',
  'granted',
  'declined',
]);

export const rewardSubmissionsTable = pgTable('reward_submissions', {
  id: id()
    .primaryKey()
    .$defaultFn(() => generateId()),
  userId: id()
    .notNull()
    .references(() => usersTable.id, {onDelete: 'cascade'}),
  submission: pgRewardSubmissionColumn().notNull(),
  status: rewardSubmissionStatus().notNull(),
  /* Should be set after granting, so we can keep track. */
  grantedBonusCreditsId: id().references(() => bonusCreditsTable.id, {
    onDelete: 'cascade',
  }),
  createdAt: timestamp().notNull().defaultNow(),
  updatedAt: timestamp()
    .notNull()
    .defaultNow()
    .$onUpdate(() => new Date()),
});
export const generationAbortionsTable = pgTable(
  'generation_abortions',
  {
    abortToken: id().primaryKey(),
    createdAt: timestamp().notNull(),
  },
  (t) => [index().on(t.createdAt)],
);

// ──────────────────────────────────────────────────────────────────────────
// Taste engine — admin panel for batched generation + rating + dataset export
// See docs/taste-engine.md
// ──────────────────────────────────────────────────────────────────────────

export const adminEmailsTable = pgTable('admin_emails', {
  email: text().primaryKey(),
  addedBy: id().references(() => usersTable.id, {onDelete: 'set null'}),
  createdAt: timestamp().notNull().defaultNow(),
});

export const tasteReferencesTable = pgTable(
  'taste_references',
  {
    id: id().primaryKey().$defaultFn(generateId),
    filename: varchar({length: 255}).notNull().unique(),
    content: text().notNull(),
    // Optional image attachment uploaded to Vercel Blob. Shown in the
    // editor and (planned) as a thumbnail in the section list.
    imageUrl: text(),
    imageBlobPathname: text(),
    createdBy: id()
      .notNull()
      .references(() => usersTable.id, {onDelete: 'cascade'}),
    createdAt: timestamp().notNull().defaultNow(),
    updatedAt: timestamp()
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
  },
  (t) => [index().on(t.createdBy)],
);

export const tasteSwarmKindEnum = pgEnum('taste_swarm_kind', [
  'swarm',
  'modelTest',
]);

export const tasteSwarmsTable = pgTable(
  'taste_swarms',
  {
    id: id().primaryKey().$defaultFn(generateId),
    kind: tasteSwarmKindEnum().notNull().default('swarm'),
    name: varchar({length: 255}).notNull(),
    prompt: text().notNull(),
    agentCount: integer().notNull(),
    model: modelEnum().notNull(),
    serviceTier: text().notNull(),
    // Fine-tune test runs (kind = 'modelTest') store the inference model id
    // here. Provider is inferred from the string at call-time: Fireworks ids
    // start with `accounts/…` (e.g. `accounts/X/models/Y#accounts/X/deployments/Z`)
    // while Together ids are free-form (e.g. `vova_a229/Qwen3-8B-…`). Null on
    // regular swarm rows.
    togetherModel: text(),
    wrapChatML: boolean(),
    temperature: doublePrecision(),
    topP: doublePrecision(),
    maxTokens: integer(),
    createdBy: id()
      .notNull()
      .references(() => usersTable.id, {onDelete: 'cascade'}),
    createdAt: timestamp().notNull().defaultNow(),
  },
  (t) => [index().on(t.createdBy, t.createdAt.desc())],
);

export const tasteSwarmReferenceSnapshotsTable = pgTable(
  'taste_swarm_reference_snapshots',
  {
    swarm: id()
      .notNull()
      .references(() => tasteSwarmsTable.id, {onDelete: 'cascade'}),
    filename: varchar({length: 255}).notNull(),
    content: text().notNull(),
  },
  (t) => [primaryKey({columns: [t.swarm, t.filename]})],
);

export const tasteSwarmAgentStatusEnum = pgEnum('taste_swarm_agent_status', [
  'pending',
  'running',
  'success',
  'failed',
  'aborted',
]);

export const tasteSwarmAgentsTable = pgTable(
  'taste_swarm_agents',
  {
    id: id().primaryKey().$defaultFn(generateId),
    swarm: id()
      .notNull()
      .references(() => tasteSwarmsTable.id, {onDelete: 'cascade'}),
    index: integer().notNull(),
    flow: id().references(() => flowsTable.id, {onDelete: 'set null'}),
    flowVersion: id().references(() => flowVersionsTable.id, {
      onDelete: 'set null',
    }),
    screenId: text(),
    status: tasteSwarmAgentStatusEnum().notNull().default('pending'),
    startedAt: timestamp().notNull().defaultNow(),
    finishedAt: timestamp(),
    errorMessage: text(),
  },
  (t) => [
    index().on(t.swarm, t.index),
    index().on(t.flow),
    index().on(t.status),
  ],
);

export const tasteSwarmAgentRatingEnum = pgEnum('taste_swarm_agent_rating', [
  'liked',
  'disliked',
]);

export const tasteSwarmAgentRatingsTable = pgTable(
  'taste_swarm_agent_ratings',
  {
    agent: id()
      .primaryKey()
      .references(() => tasteSwarmAgentsTable.id, {onDelete: 'cascade'}),
    rating: tasteSwarmAgentRatingEnum().notNull(),
    ratedBy: id()
      .notNull()
      .references(() => usersTable.id, {onDelete: 'cascade'}),
    ratedAt: timestamp().notNull().defaultNow(),
  },
  (t) => [index().on(t.ratedBy), index().on(t.rating)],
);

export const tasteDatasetsTable = pgTable(
  'taste_datasets',
  {
    id: id().primaryKey().$defaultFn(generateId),
    name: varchar({length: 255}).notNull(),
    createdBy: id()
      .notNull()
      .references(() => usersTable.id, {onDelete: 'cascade'}),
    createdAt: timestamp().notNull().defaultNow(),
    updatedAt: timestamp()
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
  },
  (t) => [index().on(t.createdAt.desc())],
);

export const tasteDatasetAgentsTable = pgTable(
  'taste_dataset_agents',
  {
    dataset: id()
      .notNull()
      .references(() => tasteDatasetsTable.id, {onDelete: 'cascade'}),
    agent: id()
      .notNull()
      .references(() => tasteSwarmAgentsTable.id, {onDelete: 'cascade'}),
    addedBy: id()
      .notNull()
      .references(() => usersTable.id, {onDelete: 'cascade'}),
    addedAt: timestamp().notNull().defaultNow(),
  },
  (t) => [primaryKey({columns: [t.dataset, t.agent]}), index().on(t.agent)],
);

export const tastePalettesTable = pgTable(
  'taste_palettes',
  {
    id: id().primaryKey().$defaultFn(generateId),
    filename: varchar({length: 255}).notNull().unique(),
    content: text().notNull(),
    createdBy: id()
      .notNull()
      .references(() => usersTable.id, {onDelete: 'cascade'}),
    createdAt: timestamp().notNull().defaultNow(),
    updatedAt: timestamp()
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
  },
  (t) => [index().on(t.createdBy)],
);

export const tasteSwarmPaletteSnapshotsTable = pgTable(
  'taste_swarm_palette_snapshots',
  {
    swarm: id()
      .notNull()
      .references(() => tasteSwarmsTable.id, {onDelete: 'cascade'}),
    filename: varchar({length: 255}).notNull(),
    content: text().notNull(),
  },
  (t) => [primaryKey({columns: [t.swarm, t.filename]})],
);

// Image-style guides. Mirror palettes: a pool of MD files, one assigned at
// random per agent and mounted into /skills/images/. Overrides the default
// photo-styles.md guidance for that turn.
export const tasteImagesTable = pgTable(
  'taste_images',
  {
    id: id().primaryKey().$defaultFn(generateId),
    filename: varchar({length: 255}).notNull().unique(),
    content: text().notNull(),
    createdBy: id()
      .notNull()
      .references(() => usersTable.id, {onDelete: 'cascade'}),
    createdAt: timestamp().notNull().defaultNow(),
    updatedAt: timestamp()
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
  },
  (t) => [index().on(t.createdBy)],
);

export const tasteSwarmImageSnapshotsTable = pgTable(
  'taste_swarm_image_snapshots',
  {
    swarm: id()
      .notNull()
      .references(() => tasteSwarmsTable.id, {onDelete: 'cascade'}),
    filename: varchar({length: 255}).notNull(),
    content: text().notNull(),
  },
  (t) => [primaryKey({columns: [t.swarm, t.filename]})],
);

export const tasteTypefaceStyleEnum = pgEnum('taste_typeface_style', [
  'normal',
  'italic',
]);

export const tasteTypefacesTable = pgTable(
  'taste_typefaces',
  {
    id: id().primaryKey().$defaultFn(generateId),
    family: varchar({length: 255}).notNull(),
    weight: integer(), // null = variable
    style: tasteTypefaceStyleEnum().notNull().default('normal'),
    format: varchar({length: 8}).notNull(), // woff2 | woff | ttf | otf
    blobUrl: text().notNull(),
    blobPathname: text().notNull(),
    originalFilename: varchar({length: 255}).notNull(),
    sizeBytes: integer().notNull(),
    createdBy: id()
      .notNull()
      .references(() => usersTable.id, {onDelete: 'cascade'}),
    createdAt: timestamp().notNull().defaultNow(),
  },
  (t) => [index().on(t.family), index().on(t.createdBy)],
);

export const tasteFontsTable = pgTable(
  'taste_fonts',
  {
    id: id().primaryKey().$defaultFn(generateId),
    filename: varchar({length: 255}).notNull().unique(),
    content: text().notNull(),
    // Optional link to an uploaded typeface. Set when the MD was created via
    // the "+" upload flow. Null for hand-authored MDs describing bundled
    // fonts. Deleting the typeface deletes its MD too.
    typeface: id().references(() => tasteTypefacesTable.id, {
      onDelete: 'cascade',
    }),
    createdBy: id()
      .notNull()
      .references(() => usersTable.id, {onDelete: 'cascade'}),
    createdAt: timestamp().notNull().defaultNow(),
    updatedAt: timestamp()
      .notNull()
      .defaultNow()
      .$onUpdate(() => new Date()),
  },
  (t) => [index().on(t.createdBy), index().on(t.typeface)],
);

export const tasteSwarmFontSnapshotsTable = pgTable(
  'taste_swarm_font_snapshots',
  {
    swarm: id()
      .notNull()
      .references(() => tasteSwarmsTable.id, {onDelete: 'cascade'}),
    filename: varchar({length: 255}).notNull(),
    content: text().notNull(),
  },
  (t) => [primaryKey({columns: [t.swarm, t.filename]})],
);

export const usersInsertSchema = createInsertSchema(usersTable);
export const subscriptionInsertSchema = createInsertSchema(subscriptionsTable);
export const flowsInsertSchema = createInsertSchema(flowsTable);
export const flowsUpdateSchema = createUpdateSchema(flowsTable);
export const screensUpdateSchema = createUpdateSchema(screensTable);
export const likesInsertSchema = createInsertSchema(likesTable);
export const figmaTokensInsertSchema = createInsertSchema(figmaTokensTable);
export const teamsUpdateSchema = createUpdateSchema(teamsTable);
export const teamSubscriptionsInsertSchema = createInsertSchema(
  teamSubscriptionsTable,
);

export const dbGenTypes = genTypeEnum.enumValues;
export const dbStructureGenerationModels = modelEnum.enumValues;
export const teamRoleSchema = z.enum(teamRoleEnum.enumValues);

// Note: the override is necessary because the type is wrong for some reason
export const themesUpdateSchema = createUpdateSchema(themesTable).extend({
  fontFamilyBody: z.enum(fontFamilyValues).optional(),
});

export type Theme = typeof themesTable.$inferSelect;
export type Flow = typeof flowsTable.$inferSelect;
export type Structure = typeof structuresTable.$inferSelect;
export type Message = typeof messagesTable.$inferSelect;
export type Screen = typeof screensTable.$inferSelect;
export type ComponentScreenPosition =
  typeof componentScreenPositionsTable.$inferSelect;
export type FlowUpdate = z.infer<typeof flowsUpdateSchema>;
export type GenType = (typeof genTypeEnum.enumValues)[number];
export type {ScreenPositions} from '@/app/db/screenPositions';

export type TasteReference = typeof tasteReferencesTable.$inferSelect;
export type TastePalette = typeof tastePalettesTable.$inferSelect;
export type TasteImage = typeof tasteImagesTable.$inferSelect;
export type TasteFont = typeof tasteFontsTable.$inferSelect;
export type TasteTypeface = typeof tasteTypefacesTable.$inferSelect;
export type TasteTypefaceStyle =
  (typeof tasteTypefaceStyleEnum.enumValues)[number];
export type TasteSwarm = typeof tasteSwarmsTable.$inferSelect;
export type TasteSwarmKind = (typeof tasteSwarmKindEnum.enumValues)[number];
export type TasteSwarmAgent = typeof tasteSwarmAgentsTable.$inferSelect;
export type TasteSwarmAgentRating =
  typeof tasteSwarmAgentRatingsTable.$inferSelect;
export type TasteSwarmAgentStatus =
  (typeof tasteSwarmAgentStatusEnum.enumValues)[number];
export type TasteSwarmAgentRatingValue =
  (typeof tasteSwarmAgentRatingEnum.enumValues)[number];
